import Organization from "../models/serviceModels/Organization";
import Ethnicity from "../models/serviceModels/Ethnicity";
import FormField from "../models/serviceModels/FormField";
import Gender from "../models/serviceModels/Gender";
import Race from "../models/serviceModels/Race";
import { demographicsOrgSettingsEnabled, organizationProfileFieldConfigurationEnabled } from "./features";

const LEGACY_VISIBLE_FIELDS = ['pronouns', 'gender', 'ethnicity', 'race'];
const CONTACT_VISIBLE_FIELDS = ['address', 'city', 'state', 'zip', 'phone', 'email'];
const DEFAULT_VISIBLE_FIELDS = [...LEGACY_VISIBLE_FIELDS, ...CONTACT_VISIBLE_FIELDS];
const MALE_GENDER_NAMES = ['male', 'man'];
const FEMALE_GENDER_NAMES = ['female', 'woman'];
const BINARY_GENDER_NAMES = [...MALE_GENDER_NAMES, ...FEMALE_GENDER_NAMES];
const PREFERRED_OTHER_GENDER_NAMES = ['some other gender identity', 'other'];
const EXCLUDED_COMBINED_RACE_ETHNICITY_NAMES = [
    'not hispanic or latino',
    'prefer not to answer',
    'some other race/ethnicity',
    'some other race or ethnicity'
];
const DEFAULT_GENDER_PREVIEW_OPTIONS = ['Male', 'Female', 'Some Other Gender Identity'];
const SIMPLIFIED_GENDER_PREVIEW_OPTIONS = ['Male', 'Female', 'Other'];
const COMBINED_RACE_ETHNICITY_PREVIEW_OPTIONS = [
    'American Indian or Alaska Native',
    'Asian',
    'Black',
    'Hispanic or Latino',
    'Middle Eastern or North African',
    'Native Hawaiian or Pacific Islander',
    'White',
    'Multiracial or Biracial'
];
const COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS = [
    {
        label: 'American Indian or Alaska Native',
        source: 'race',
        names: ['american indian or alaska native', 'american indian, alaska native, indigenous, native american and/or first nations', 'american indian/alaska native', 'american indian', 'alaska native']
    },
    {
        label: 'Asian',
        source: 'race',
        names: ['asian'],
        fallbackNames: ['asian pacific islander desi american (apida) and/or native hawai\'ian', 'asian, pacific islander, desi american, and/or hawaiian']
    },
    {
        label: 'Black',
        source: 'race',
        names: ['black or african american', 'black and/or african american', 'black', 'african american']
    },
    {
        label: 'Hispanic or Latino',
        source: 'ethnicity',
        names: ['hispanic or latino', 'latinx/a/o and/or hispanic', 'hispanic/latino', 'hispanic', 'latino']
    },
    {
        label: 'Middle Eastern or North African',
        source: 'race',
        names: ['middle eastern or north african', 'middle eastern and/or north african (mena)', 'middle eastern/north african', 'middle eastern', 'north african']
    },
    {
        label: 'Native Hawaiian or Pacific Islander',
        source: 'race',
        names: ['native hawaiian or pacific islander', 'native hawaiian or other pacific islander', 'native hawaiian/pacific islander', 'pacific islander'],
        fallbackNames: ['asian pacific islander desi american (apida) and/or native hawai\'ian', 'asian, pacific islander, desi american, and/or hawaiian']
    },
    {
        label: 'White',
        source: 'race',
        names: ['white', 'white and/or european american']
    },
    {
        label: 'Multiracial or Biracial',
        source: 'race',
        names: ['multiracial or biracial', 'multiracial and/or biracial', 'multiracial', 'biracial', 'two or more races', 'two or more']
    }
] as const;

type CombinedRaceEthnicityDefinition = typeof COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS[number];
type DemographicField = 'pronouns' | 'gender' | 'ethnicity' | 'race' | 'address' | 'city' | 'state' | 'zip' | 'phone' | 'email';
type DemographicsConfig = Pick<Organization, 'demographicsVisibleFields' | 'demographicsGenderMode' | 'demographicsRaceMode'>;

const getVisibleDemographicsFields = (config?: DemographicsConfig): string[] => {
    if (!demographicsOrgSettingsEnabled) {
        return DEFAULT_VISIBLE_FIELDS;
    }

    const visibleFields = config?.demographicsVisibleFields !== undefined
        ? config.demographicsVisibleFields
        : DEFAULT_VISIBLE_FIELDS;

    return organizationProfileFieldConfigurationEnabled
        ? visibleFields
        : Array.from(new Set([...visibleFields, ...CONTACT_VISIBLE_FIELDS]));
};

const isDemographicVisible = (config: DemographicsConfig | undefined, field: DemographicField): boolean => {
    return getVisibleDemographicsFields(config).includes(field);
};

const getDemographicsGenderMode = (config?: DemographicsConfig): 'current' | 'simplified' => {
    if (!demographicsOrgSettingsEnabled) {
        return 'current';
    }

    return config?.demographicsGenderMode || 'current';
};

const getDemographicsRaceMode = (config?: DemographicsConfig): 'separate' | 'combined' => {
    if (!demographicsOrgSettingsEnabled) {
        return 'separate';
    }

    return config?.demographicsRaceMode || 'separate';
};

const getDisplayGenderName = (genderName: string | undefined | null, config?: DemographicsConfig) => {
    if (!genderName) {
        return '';
    }

    if (getDemographicsGenderMode(config) !== 'simplified') {
        return genderName;
    }

    const normalized = genderName.trim().toLowerCase();
    if (normalized === 'unanswered') {
        return 'Unanswered';
    }

    if (MALE_GENDER_NAMES.includes(normalized)) {
        return 'Male';
    }

    if (FEMALE_GENDER_NAMES.includes(normalized)) {
        return 'Female';
    }

    return 'Other';
};

const normalizeDemographicsOptionName = (name: string | undefined | null) => {
    return (name || '').trim().toLowerCase().replace(/[\u2018\u2019`]/g, "'").replace(/\s+/g, ' ');
};

const slugifyDemographicsOptionName = (name: string) => {
    return normalizeDemographicsOptionName(name).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
};

const compactDemographicsOptionName = (name: string) => {
    return normalizeDemographicsOptionName(name).replace(/[^a-z0-9]+/g, '');
};

const isExcludedCombinedRaceEthnicityName = (name: string | undefined | null) => {
    const normalizedName = normalizeDemographicsOptionName(name);
    if (!normalizedName) {
        return true;
    }

    return EXCLUDED_COMBINED_RACE_ETHNICITY_NAMES.includes(normalizedName);
};

const isOmbOnlyRaceName = (name: string | undefined | null) => {
    const normalizedName = normalizeDemographicsOptionName(name);
    return normalizedName === 'asian'
        || normalizedName === 'native hawaiian or pacific islander';
};

const combinedRaceEthnicityFallbackMatches = (
    definition: CombinedRaceEthnicityDefinition,
    normalizedName: string
) => {
    const fallbackNames = (definition as { fallbackNames?: readonly string[] }).fallbackNames;
    if (!fallbackNames) {
        return false;
    }

    if (fallbackNames.includes(normalizedName)) {
        return true;
    }

    const compactName = compactDemographicsOptionName(normalizedName);
    return compactName.includes('asian')
        && compactName.includes('pacificislander')
        && (compactName.includes('desiamerican') || compactName.includes('apida') || compactName.includes('hawaiian'));
};

const isFallbackNameForAnotherCombinedDefinition = (
    definition: CombinedRaceEthnicityDefinition,
    normalizedName: string
) => {
    return COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS.some(otherDefinition =>
        otherDefinition !== definition && combinedRaceEthnicityFallbackMatches(otherDefinition, normalizedName)
    );
};

const combinedRaceEthnicityDefinitionMatches = (
    definition: CombinedRaceEthnicityDefinition,
    normalizedName: string
) => {
    if (isFallbackNameForAnotherCombinedDefinition(definition, normalizedName)) {
        return false;
    }

    if (definition.label === 'Asian' && normalizedName.includes('asian')) {
        return true;
    }

    return (definition.names as readonly string[]).includes(normalizedName);
};

const createOmbRaceEthnicityValue = (
    definition: CombinedRaceEthnicityDefinition,
    fallbackRaceId?: number | string
) => {
    const baseValue = `omb:${slugifyDemographicsOptionName(definition.label)}`;
    return fallbackRaceId ? `${baseValue}:r:${fallbackRaceId}` : baseValue;
};

const getFallbackRaceIdFromOmbValue = (value: string | undefined | null) => {
    const match = (value || '').match(/:r:(\d+)$/);
    return match ? Number(match[1]) : undefined;
};

const getFallbackRaceIdForOmbSelection = (
    selection: { label: string; value: string; },
    races: Race[]
) => {
    const embeddedFallbackId = getFallbackRaceIdFromOmbValue(selection.value);
    if (embeddedFallbackId) {
        return embeddedFallbackId;
    }

    const selectionSlug = selection.value.startsWith('omb:')
        ? selection.value.split(':')[1]
        : slugifyDemographicsOptionName(selection.label);

    const definition = COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS.find(def =>
        slugifyDemographicsOptionName(def.label) === selectionSlug
    );
    if (!definition) {
        return undefined;
    }

    return races.find(race => combinedRaceEthnicityFallbackMatches(
        definition,
        normalizeDemographicsOptionName(race.name)
    ))?.id;
};

const getCombinedRaceEthnicityDefinitionMatchesForName = (name: string | undefined | null) => {
    const normalizedName = normalizeDemographicsOptionName(name);
    if (!normalizedName || isExcludedCombinedRaceEthnicityName(normalizedName)) {
        return [];
    }

    const directDefinitions = COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS.filter(def =>
        combinedRaceEthnicityDefinitionMatches(def, normalizedName)
    );
    if (directDefinitions.length !== 0) {
        return directDefinitions.map(definition => ({
            definition,
            matchType: 'direct' as const
        }));
    }

    const fallbackDefinitions = COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS.filter(def =>
        combinedRaceEthnicityFallbackMatches(def, normalizedName)
    );
    return fallbackDefinitions.map(definition => ({
        definition,
        matchType: 'fallback' as const
    }));
};

const getCombinedRaceEthnicityDefinitionForName = (name: string | undefined | null) => {
    return getCombinedRaceEthnicityDefinitionMatchesForName(name)[0];
};

const getCombinedRaceEthnicityLabel = (name: string | undefined | null) => {
    if (isExcludedCombinedRaceEthnicityName(name)) {
        return '';
    }

    const match = getCombinedRaceEthnicityDefinitionForName(name);
    return match?.definition.label || name?.trim() || '';
};

const isPositiveCombinedEthnicityName = (ethnicityName: string | undefined | null) => {
    if (!ethnicityName) {
        return false;
    }

    return !isExcludedCombinedRaceEthnicityName(ethnicityName);
};

const getPreferredOtherGender = (genders: Gender[]) => {
    for (const preferredName of PREFERRED_OTHER_GENDER_NAMES) {
        const matchingGender = genders.find(g => g.name.trim().toLowerCase() === preferredName);
        if (matchingGender) {
            return matchingGender;
        }
    }

    return genders.find(g => !BINARY_GENDER_NAMES.includes(g.name.trim().toLowerCase()));
};

const getSimplifiedGenderOptionValue = (genders: Gender[], target: 'male' | 'female' | 'other') => {
    if (target === 'male') {
        return genders.find(g => MALE_GENDER_NAMES.includes(g.name.trim().toLowerCase()))?.id?.toString() || '';
    }

    if (target === 'female') {
        return genders.find(g => FEMALE_GENDER_NAMES.includes(g.name.trim().toLowerCase()))?.id?.toString() || '';
    }

    const fallbackOther = getPreferredOtherGender(genders);
    return fallbackOther?.id?.toString() || '';
};

const getGenderOptionsForOrganization = (genders: Gender[], config?: DemographicsConfig): { label: string; value: string; }[] => {
    if (getDemographicsGenderMode(config) !== 'simplified') {
        return genders.map(g => ({
            label: g.name,
            value: g.id.toString()
        }));
    }

    const maleValue = getSimplifiedGenderOptionValue(genders, 'male');
    const femaleValue = getSimplifiedGenderOptionValue(genders, 'female');
    const otherValue = getSimplifiedGenderOptionValue(genders, 'other');

    return [
        { label: 'Male', value: maleValue },
        { label: 'Female', value: femaleValue },
        { label: 'Other', value: otherValue }
    ].filter(o => !!o.value);
};

const getStoredGenderValueForOrganization = ({
    genderId,
    genderName,
    genders
}: {
    genderId?: number | string | null;
    genderName?: string | null;
    genders: Gender[];
}, config?: DemographicsConfig) => {
    if (getDemographicsGenderMode(config) !== 'simplified') {
        return genderId?.toString() || '';
    }

    const normalized = (genderName || '').trim().toLowerCase();
    if (!genderId && !normalized) {
        return '';
    }

    if (MALE_GENDER_NAMES.includes(normalized)) {
        return getSimplifiedGenderOptionValue(genders, 'male');
    }

    if (FEMALE_GENDER_NAMES.includes(normalized)) {
        return getSimplifiedGenderOptionValue(genders, 'female');
    }

    return getSimplifiedGenderOptionValue(genders, 'other');
};

const getGenderPreviewOptions = (config?: DemographicsConfig) => {
    return getDemographicsGenderMode(config) === 'simplified'
        ? SIMPLIFIED_GENDER_PREVIEW_OPTIONS
        : DEFAULT_GENDER_PREVIEW_OPTIONS;
};

const isCombinedRaceMode = (config?: DemographicsConfig) => getDemographicsRaceMode(config) === 'combined';

const getEthnicityFieldVisible = (config?: DemographicsConfig) => {
    return isDemographicVisible(config, 'ethnicity') && !isCombinedRaceMode(config);
};

const getRaceOptionsForOrganization = (races: Race[], ethnicities: Ethnicity[], config?: DemographicsConfig): { label: string; value: string; }[] => {
    if (!isCombinedRaceMode(config)) {
        return races
            .filter(r => !isOmbOnlyRaceName(r.name))
            .map(r => ({
                label: r.name,
                value: r.id.toString()
            }));
    }

    return COMBINED_RACE_ETHNICITY_OPTION_DEFINITIONS.map(definition => {
        const sourceOptions = definition.source === 'ethnicity' ? ethnicities : races;
        const matchingOption = sourceOptions.find(option => {
            const normalizedName = normalizeDemographicsOptionName(option.name);
            return !isExcludedCombinedRaceEthnicityName(normalizedName)
                && combinedRaceEthnicityDefinitionMatches(definition, normalizedName);
        });
        const sourcePrefix = definition.source === 'ethnicity' ? 'e' : 'r';

        const fallbackOption = sourceOptions.find(option => combinedRaceEthnicityFallbackMatches(
            definition,
            normalizeDemographicsOptionName(option.name)
        ));

        return {
            label: definition.label,
            value: matchingOption
                ? `${sourcePrefix}:${matchingOption.id}`
                : createOmbRaceEthnicityValue(definition, fallbackOption?.id)
        };
    });
};

const getEthnicityPreviewOptions = (ethnicities: Ethnicity[], config?: DemographicsConfig) => {
    if (!getEthnicityFieldVisible(config)) {
        return [];
    }

    return ethnicities.map(ethnicity => ethnicity.name);
};

const getRacePreviewOptions = (races: Race[], config?: DemographicsConfig) => {
    if (isCombinedRaceMode(config)) {
        return COMBINED_RACE_ETHNICITY_PREVIEW_OPTIONS;
    }

    return races.filter(race => !isOmbOnlyRaceName(race.name)).map(race => race.name);
};

const getStoredRaceSelectionsForOrganization = ({
    ethnicityId,
    ethnicityName,
    races
}: {
    ethnicityId?: number | string | null;
    ethnicityName?: string | null;
    races: Array<{ id: number; name: string; }>;
}, config?: DemographicsConfig) => {
    if (!isCombinedRaceMode(config)) {
        return races.map(r => ({
            label: r.name,
            value: r.id.toString()
        }));
    }

    const combinedSelections = races.flatMap(r => {
        const matches = getCombinedRaceEthnicityDefinitionMatchesForName(r.name);
        if (matches.length === 0) {
            return [{
                label: getCombinedRaceEthnicityLabel(r.name),
                value: `r:${r.id}`
            }];
        }

        return matches.map(match => ({
            label: match.definition.label,
            value: match.matchType === 'fallback'
                ? createOmbRaceEthnicityValue(match.definition, r.id)
                : `r:${r.id}`
        }));
    }).filter(r => !!r.label);

    const positiveEthnicityName = isPositiveCombinedEthnicityName(ethnicityName)
        ? ethnicityName || undefined
        : undefined;

    if (ethnicityId && positiveEthnicityName) {
        return [
            {
                label: getCombinedRaceEthnicityLabel(positiveEthnicityName),
                value: `e:${ethnicityId}`
            },
            ...combinedSelections
        ].filter(r => !!r.label);
    }

    return combinedSelections;
};

const splitRaceSelectionsForOrganization = (
    selections: Array<{ label: string; value: string; }>,
    config?: DemographicsConfig,
    races: Race[] = []
) => {
    if (!isCombinedRaceMode(config)) {
        const raceIds = selections
            .map(selection => Number(selection.value))
            .filter(value => !isNaN(value));

        return {
            ethnicityId: undefined,
            raceIds: raceIds.filter((raceId, index) => raceIds.indexOf(raceId) === index)
        };
    }

    const ethnicitySelection = selections.find(selection => selection.value.startsWith('e:'));
    const ethnicityId = ethnicitySelection ? Number(ethnicitySelection.value.split(':')[1]) : undefined;
    const raceIds = selections
        .filter(selection => selection.value.startsWith('r:'))
        .map(selection => Number(selection.value.split(':')[1]))
        .filter(value => !isNaN(value));
    const legacyRaceIds = selections
        .filter(selection =>
            !selection.value.startsWith('e:')
            && !selection.value.startsWith('r:')
            && !selection.value.startsWith('omb:')
        )
        .map(selection => Number(selection.value))
        .filter(value => !isNaN(value));
    const fallbackRaceIds = selections
        .filter(selection =>
            selection.value.startsWith('omb:')
            || (
                !selection.value.startsWith('e:')
                && !selection.value.startsWith('r:')
                && isNaN(Number(selection.value))
            )
        )
        .map(selection => getFallbackRaceIdForOmbSelection(selection, races))
        .filter((value): value is number => value !== undefined && !isNaN(value));
    const allRaceIds = [...raceIds, ...legacyRaceIds, ...fallbackRaceIds];

    return {
        ethnicityId,
        raceIds: allRaceIds.filter((raceId, index) => allRaceIds.indexOf(raceId) === index)
    };
};

const getDisplayRaceNames = ({
    ethnicityName,
    races
}: {
    ethnicityName?: string | null;
    races: Array<{ name: string; }>;
}, config?: DemographicsConfig) => {
    if (isCombinedRaceMode(config)) {
        const labels: string[] = [];
        const normalizedLabels = new Set<string>();

        const appendUniqueLabel = (label: string | undefined | null) => {
            const normalized = label?.trim();
            if (!normalized) {
                return;
            }

            const key = normalized.toLowerCase();
            if (normalizedLabels.has(key)) {
                return;
            }

            normalizedLabels.add(key);
            labels.push(normalized);
        };

        if (isPositiveCombinedEthnicityName(ethnicityName)) {
            appendUniqueLabel(getCombinedRaceEthnicityLabel(ethnicityName));
        }

        races.forEach(race => {
            const matches = getCombinedRaceEthnicityDefinitionMatchesForName(race.name);
            if (matches.length === 0) {
                appendUniqueLabel(getCombinedRaceEthnicityLabel(race.name));
                return;
            }

            matches.forEach(match => appendUniqueLabel(match.definition.label));
        });
        return labels;
    }

    return races.map(r => r.name);
};

const getRaceFieldLabel = (config?: DemographicsConfig) => {
    return isCombinedRaceMode(config) ? 'Race/Ethnicity' : 'Race';
};

const normalizeGenderOptionLabel = (label: string) => {
    const normalized = label.trim().toLowerCase();
    if (normalized === 'man') {
        return 'Male';
    }

    if (normalized === 'woman') {
        return 'Female';
    }

    return label;
};

const normalizeDemographicsFormField = (field: FormField): FormField => {
    const normalizedLabel = field.name === 'main_profile_race' && /^race\s*\/\s*ethnicity$/i.test(field.label)
        ? 'Race/Ethnicity'
        : field.label;

    const normalizedOptions = field.name === 'main_profile_gender' || field.name === 'gender'
        ? field.options?.map(option => ({
            ...option,
            label: normalizeGenderOptionLabel(option.label)
        }))
        : field.options;

    if (field.name !== 'main_profile_family_members' || !field.schema) {
        return {
            ...field,
            label: normalizedLabel,
            options: normalizedOptions
        };
    }

    let parsedSchema: Array<{
        fieldLabel: string;
        fieldName: string;
        options?: Array<{ label: string; value: string; order: number }>;
    }> = [];

    try {
        parsedSchema = JSON.parse(field.schema);
    } catch (error) {
        console.warn('Failed to parse demographics schema for form field normalization', error);
        return {
            ...field,
            label: normalizedLabel,
            options: normalizedOptions
        };
    }

    return {
        ...field,
        label: normalizedLabel,
        options: normalizedOptions,
        schema: JSON.stringify(parsedSchema.map(schemaField => {
            if (schemaField.fieldName === 'gender' && schemaField.options) {
                return {
                    ...schemaField,
                    options: schemaField.options.map(option => ({
                        ...option,
                        label: normalizeGenderOptionLabel(option.label)
                    }))
                };
            }

            return schemaField;
        }))
    };
};

export {
    COMBINED_RACE_ETHNICITY_PREVIEW_OPTIONS,
    DEFAULT_VISIBLE_FIELDS,
    type DemographicField,
    type DemographicsConfig,
    getVisibleDemographicsFields,
    isDemographicVisible,
    getDemographicsGenderMode,
    getDemographicsRaceMode,
    getDisplayGenderName,
    getGenderOptionsForOrganization,
    getGenderPreviewOptions,
    getStoredGenderValueForOrganization,
    getEthnicityFieldVisible,
    getEthnicityPreviewOptions,
    getRaceOptionsForOrganization,
    getRacePreviewOptions,
    getStoredRaceSelectionsForOrganization,
    splitRaceSelectionsForOrganization,
    getCombinedRaceEthnicityLabel,
    getDisplayRaceNames,
    getRaceFieldLabel,
    normalizeDemographicsFormField
};
